/**
 * TabsContagem - Component for Counting System (Epic 18 - Phase 3)
 *
 * Three tabs:
 * 1. Planejamento - Planning information (antes: Agendamento)
 * 2. Itens da Contagem - Counting items with Excel import
 * 3. Cirurgias a Faturar - Surgeries to invoice
 *
 * Epic 53: Two import buttons for Ordem and Resultado de Contagem
 * Epic Reorganização Abas: Renamed Agendamento to Planejamento
 */
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calendar, Download, FileText, Info } from 'lucide-react';
import ImportExcelModal from './ImportExcelModal';
import ImportOrdemContagemModal from './ImportOrdemContagemModal';
import ImportResultadoContagemModal from './ImportResultadoContagemModal';
import TabelaDadosContagem from './TabelaDadosContagem';
import { ItemContagemImportado } from '../../services/contagemService';

// Column definitions for tooltips
const ORDEM_COLUMNS = [
  { col: 'A', name: 'Código Cliente', required: true },
  { col: 'B', name: 'Regime Especial', required: false },
  { col: 'C', name: 'Código', required: true },
  { col: 'D', name: 'Descrição', required: false },
  { col: 'E', name: 'Lote', required: true },
  { col: 'F', name: 'Validade', required: true },
  { col: 'G', name: 'Qtde (Sistema)', required: true },
  { col: 'H', name: 'Coletar', required: false },
];

const RESULTADO_COLUMNS = [
  { col: 'A', name: 'Código Cliente', required: true },
  { col: 'B', name: 'Código', required: true },
  { col: 'C', name: 'Descrição', required: false },
  { col: 'D', name: 'Lote', required: true },
  { col: 'E', name: 'Validade', required: true },
  { col: 'F', name: 'Qtde (Contada)', required: true },
];

interface TabsContagemProps {
  jobId: number;
  jobTitulo: string;
  hospitalNome: string;
}

// Tooltip component for column info
// position: 'left' opens to the left (for buttons on right side), 'right' opens to the right (default)
function ColumnTooltip({ columns, color, position = 'right' }: { columns: typeof ORDEM_COLUMNS; color: 'emerald' | 'blue'; position?: 'left' | 'right' }) {
  const [isVisible, setIsVisible] = useState(false);
  const bgColor = color === 'emerald' ? 'bg-emerald-50 dark:bg-emerald-900/90' : 'bg-blue-50 dark:bg-blue-900/90';
  const borderColor = color === 'emerald' ? 'border-emerald-300 dark:border-emerald-600' : 'border-blue-300 dark:border-blue-600';
  const iconColor = color === 'emerald' ? 'text-emerald-600 dark:text-emerald-400' : 'text-blue-600 dark:text-blue-400';
  const positionClass = position === 'left' ? 'right-0' : 'left-0';

  return (
    <div className="relative inline-block">
      <button
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => setIsVisible(!isVisible)}
        className={`p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition ${iconColor}`}
        title="Ver colunas esperadas"
      >
        <Info size={18} />
      </button>
      {isVisible && (
        <div className={`absolute z-50 ${positionClass} top-full mt-2 w-64 ${bgColor} ${borderColor} border rounded-lg shadow-xl p-3`}>
          <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2">
            Colunas esperadas:
          </div>
          <div className="space-y-1">
            {columns.map((col) => (
              <div key={col.col} className="flex items-center text-xs text-gray-600 dark:text-gray-300">
                <span className="font-mono bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded mr-2 min-w-[20px] text-center">
                  {col.col}
                </span>
                <span className={col.required ? 'font-medium' : ''}>
                  {col.name}
                  {col.required && <span className="text-red-500 ml-0.5">*</span>}
                </span>
              </div>
            ))}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
            * Campos obrigatórios
          </div>
        </div>
      )}
    </div>
  );
}

export default function TabsContagem({ jobId, jobTitulo, hospitalNome }: TabsContagemProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') || 'itens') as 'agendamento' | 'itens' | 'cirurgias';
  const [showImportModal, setShowImportModal] = useState(false);
  // Epic 53: New modal states
  const [showImportOrdemModal, setShowImportOrdemModal] = useState(false);
  const [showImportResultadoModal, setShowImportResultadoModal] = useState(false);
  const [itensImportados, setItensImportados] = useState<ItemContagemImportado[]>([]);
  const [importInfo, setImportInfo] = useState<{
    total_linhas: number;
    total_processadas: number;
    total_erros: number;
  } | null>(null);
  // Key to force reload TabelaDadosContagem
  const [reloadKey, setReloadKey] = useState(0);

  const handleImportSuccess = (dados: any) => {
    console.log('Import success:', dados);
    setImportInfo({
      total_linhas: dados.total_linhas,
      total_processadas: dados.total_processadas,
      total_erros: dados.total_erros,
    });
    // Reload items from API
    loadItensEsperados();
  };

  // Epic 53: Handler for Ordem import success
  const handleImportOrdemSuccess = (dados: any) => {
    console.log('Import Ordem success:', dados);
    setImportInfo({
      total_linhas: dados.total_linhas,
      total_processadas: dados.total_criados,
      total_erros: dados.total_erros,
    });
    // Force reload the table
    setReloadKey(prev => prev + 1);
  };

  // Epic 53: Handler for Resultado import success
  const handleImportResultadoSuccess = (dados: any) => {
    console.log('Import Resultado success:', dados);
    setImportInfo({
      total_linhas: dados.total_linhas,
      total_processadas: dados.total_atualizados,
      total_erros: dados.total_erros + (dados.total_nao_encontrados || 0),
    });
    // Force reload the table
    setReloadKey(prev => prev + 1);
  };

  const loadItensEsperados = async () => {
    // This will be called from the child component TabelaDadosContagem
    // or we can load here and pass as props
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Calendar className="text-white" size={24} />
          <span className="text-lg font-semibold text-white">Contagens e Agendamentos</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b bg-gray-50 dark:bg-gray-900/50">
        <button
          onClick={() => setSearchParams({ tab: 'agendamento' })}
          className={`px-8 py-4 font-medium text-sm transition-all ${
            activeTab === 'agendamento'
              ? 'bg-white dark:bg-gray-800 border-b-3 border-blue-500 text-blue-600 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-white/50'
          }`}
        >
          📅 Agendamento
        </button>
        <button
          onClick={() => setSearchParams({ tab: 'itens' })}
          className={`px-8 py-4 font-medium text-sm transition-all ${
            activeTab === 'itens'
              ? 'bg-white dark:bg-gray-800 border-b-3 border-blue-500 text-blue-600 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-white/50'
          }`}
        >
          📊 Dados da Contagem
        </button>
        <button
          onClick={() => setSearchParams({ tab: 'cirurgias' })}
          className={`px-8 py-4 font-medium text-sm transition-all ${
            activeTab === 'cirurgias'
              ? 'bg-white dark:bg-gray-800 border-b-3 border-blue-500 text-blue-600 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-white/50'
          }`}
        >
          🏥 Cirurgias a Faturar
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* ABA PLANEJAMENTO (antes: Agendamento) */}
        {activeTab === 'agendamento' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-6 border-2 border-blue-200 dark:border-blue-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                📋 Informações do Agendamento
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Em desenvolvimento - Informações sobre planejamento da contagem
              </p>
            </div>
          </div>
        )}

        {/* ABA DADOS DA CONTAGEM */}
        {activeTab === 'itens' && (
          <div className="space-y-6">
            {/* Epic 53: Toolbar com 2 botões de importação */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-gray-700">
              {/* Botão Esquerda - Importar Base da Contagem (Verde) */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowImportOrdemModal(true)}
                  className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-lg hover:from-emerald-600 hover:to-green-600 transition shadow-sm font-medium"
                >
                  <Download size={18} className="rotate-180" />
                  <span>Importar Base da Contagem</span>
                </button>
                <ColumnTooltip columns={ORDEM_COLUMNS} color="emerald" />
              </div>

              {/* Botão Direita - Importar Resultado Contagem (Azul) */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowImportResultadoModal(true)}
                  className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg hover:from-blue-600 hover:to-indigo-600 transition shadow-sm font-medium"
                >
                  <Download size={18} className="rotate-180" />
                  <span>Importar Resultado Contagem</span>
                </button>
                <ColumnTooltip columns={RESULTADO_COLUMNS} color="blue" position="left" />
              </div>
            </div>

            {/* Product count info */}
            <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
              <span className="font-semibold">{itensImportados.length}</span> produto(s) na contagem
            </div>

            {/* Import Info */}
            {importInfo && (
              <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-700 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-green-900 dark:text-green-400 mb-2">
                  ✅ Importação Concluída
                </h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Total de linhas:</span>
                    <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                      {importInfo.total_linhas}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Processadas:</span>
                    <span className="ml-2 font-semibold text-green-700 dark:text-green-400">
                      {importInfo.total_processadas}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Erros:</span>
                    <span className="ml-2 font-semibold text-red-700 dark:text-red-400">
                      {importInfo.total_erros}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Informações do Hospital */}
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-lg p-4 border-2 border-emerald-200 dark:border-emerald-700">
              <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                📦 Contagem - {hospitalNome}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Job: <span className="font-semibold text-emerald-700 dark:text-emerald-400">{jobTitulo}</span>
              </div>
            </div>

            {/* Tabela de Itens da Contagem */}
            <TabelaDadosContagem
              key={reloadKey}
              jobId={jobId}
              onItensLoaded={setItensImportados}
            />
          </div>
        )}

        {/* ABA CIRURGIAS A FATURAR */}
        {activeTab === 'cirurgias' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-6 border-2 border-green-200 dark:border-green-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                🏥 Cirurgias Aguardando Faturamento
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Em desenvolvimento - Lista de cirurgias pendentes de faturamento
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Importação (legacy) */}
      <ImportExcelModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        jobId={jobId}
        onImportSuccess={handleImportSuccess}
      />

      {/* Epic 53: Modal Importar Base da Contagem */}
      <ImportOrdemContagemModal
        isOpen={showImportOrdemModal}
        onClose={() => setShowImportOrdemModal(false)}
        jobId={jobId}
        onImportSuccess={handleImportOrdemSuccess}
      />

      {/* Epic 53: Modal Importar Resultado Contagem */}
      <ImportResultadoContagemModal
        isOpen={showImportResultadoModal}
        onClose={() => setShowImportResultadoModal(false)}
        jobId={jobId}
        onImportSuccess={handleImportResultadoSuccess}
      />
    </div>
  );
}
